/**
 * Client intake flow: types for steps and answers.
 */

export type TherapyType =
  | 'individual'
  | 'couples'
  | 'family'
  | 'group'
  | 'iop'
  | 'child-adolescent'
  | 'medication';

export type FormatPreference = 'in-person' | 'virtual' | 'hybrid' | 'no-preference';

export type PayType = 'insurance' | 'cash';

/** Coverage subscriber-to-beneficiary relationship (mirrors fhir-model CoverageRelationship). */
export type SubscriberRelationship = 'self' | 'spouse' | 'child' | 'other';

export type SessionType = 'intake' | 'consultation';

/**
 * Server-side reservation handles for a booking round, so a later cancel (on the overview) can
 * release that specific booking's referral slot + held calendar Slot via the ticketing bot —
 * even for a prior round, not just the current one.
 */
export interface BookingReservation {
  episodeOfCareId?: string;
  serviceRequestId?: string;
  referralTaskId?: string;
  appointmentId?: string;
}

export interface TherapyBooking {
  therapyType: TherapyType;
  sessionType?: SessionType;
  selectedProviderId: string | null;
  bookingDate: string | null;
  bookingTime: string | null;
  cancelled?: boolean;
  /** True when this booking is a screening consultation call (not a provider appointment). */
  isScreeningCall?: boolean;
  /** True when no provider was available and the client joined the waitlist (no appointment). */
  waitlisted?: boolean;
  /** Reservation handles for releasing this booking's holds on cancel. */
  reservation?: BookingReservation;
}

export interface IntakeAnswers {
  /** One or more therapy types (step 0 tiles) */
  therapyTypes: TherapyType[];
  /** Therapy types user cancelled on cost-estimates step (card stays, can restore) */
  cancelledTherapyTypes?: TherapyType[];
  /** Therapy types where no provider was available and client opted into waitlist (no appointment booked). */
  waitlistedTherapyTypes?: TherapyType[];
  /**
   * Skipped on the provider step ("No, skip this service") before confirmation — not yet
   * cancelled; user can go back and choose waitlist or skip again.
   */
  skippedAtProviderTherapyTypes?: TherapyType[];
  /** Primary/first therapy type; kept for compatibility (e.g. initial from landing) */
  therapyType: TherapyType | null;
  /** Intake (50 min) vs consultation (15 min) for current booking round */
  sessionType?: SessionType;
  topics: string[];
  specialties: string[];
  demographicPrefs: string[];
  /** Preferred language for sessions; default English */
  language: string;
  /** High-risk screening (null = unanswered) */
  screeningEmergency: boolean | null;
  screeningHospitalization: boolean | null;
  screeningSuicidalIdeation: boolean | null;
  screeningMedications: boolean | null;
  screeningSubstanceUse: boolean | null;
  screeningLegalProceedings: boolean | null;
  format: FormatPreference | null;
  /** When not available (time). Multi-select; values match webapp bookingConstraints. */
  availabilityTimeOfDay: string[];
  /** When not available (days). Multi-select; values match webapp bookingConstraints. */
  availabilityDays: string[];
  firstName: string;
  lastName: string;
  email: string;
  /** Date of birth (YYYY-MM-DD) */
  dateOfBirth: string;
  zip: string;
  /**
   * Live Medplum Patient id created after demographics (step 5). Used for Stripe SetupIntent
   * and final intake submission without requiring portal login.
   */
  intakeLeadPatientId: string | null;
  payType: PayType | null;
  /** Display name of the selected payer Organization (insurance company). */
  insuranceCompany: string | null;
  /** Selected payer Organization id — becomes Coverage.payor reference. */
  insurancePayerOrgId: string | null;
  /** Selected provider from results (MockProvider.id for now) */
  selectedProviderId: string | null;
  /** Selected date for intake (YYYY-MM-DD) */
  bookingDate: string | null;
  /** Selected time for intake (HH:mm) */
  bookingTime: string | null;
  /** High-risk screening call: ISO start of the selected team-availability slot. */
  screeningSlotStart: string | null;
  /** High-risk screening call: ISO end of the selected team-availability slot. */
  screeningSlotEnd: string | null;
  /** High-risk screening call: practitioner assigned to the selected slot (not shown to client). */
  screeningPractitionerId: string | null;
  memberId: string;
  groupId: string;
  insuranceStartDate: string;
  insuranceEndDate: string;
  /** Subscriber (policy holder) relationship to the patient. */
  subscriberRelationship: SubscriberRelationship;
  /** Subscriber demographics — required when relationship is not "self". */
  subscriberFirstName: string;
  subscriberLastName: string;
  /** Subscriber date of birth (YYYY-MM-DD). */
  subscriberDateOfBirth: string;
  /** After Stripe SetupIntent + sync (client portal / logged-in patient). */
  stripeSetupIntentId: string | null;
  stripePaymentMethodId: string | null;
  /** e.g. "Visa •••• 4242" for confirmation UI */
  stripePaymentMethodSummary: string | null;
  /**
   * User confirms payment will be arranged later (public anonymous intake, staff-assisted intake,
   * or when Stripe is unavailable). Mutually exclusive with having saved a card.
   */
  cashPaymentArrangedLater: boolean;
  /** Preferred in-person location IDs (or ['no-preference']); populated when format is in-person/hybrid */
  preferredLocationIds: string[];
  /**
   * Group Therapy cohort preference (distinct from insurance `groupId`).
   * Set on the Group selection step when `therapyTypes` includes `group`.
   */
  groupSelection: 'none' | 'help-find' | 'group';
  /** Concrete Group/{id} when groupSelection === 'group'. */
  requestedGroupId?: string;
  /** Group Program (HealthcareService) id for waitlist / screening staging. */
  requestedGroupTemplateId?: string;
  /** Display name of the chosen cohort, shown on the review step. */
  requestedGroupName?: string;
  /** 6-digit email verification code entered by the user (step 6) */
  emailVerificationCode: string;
  /** True after server-side OTP verification succeeds (step 6) */
  emailVerified?: boolean;
  /** True when email matches an existing client with a portal account */
  existingClient?: boolean;
  /** Completed booking rounds (one per therapy type) */
  bookings: TherapyBooking[];
  /** Reservation handles for the in-progress round, attached to the booking on SAVE_CURRENT_BOOKING. */
  currentReservation?: BookingReservation | null;
}

export const INITIAL_ANSWERS: IntakeAnswers = {
  therapyTypes: [],
  cancelledTherapyTypes: [],
  waitlistedTherapyTypes: [],
  skippedAtProviderTherapyTypes: [],
  therapyType: null,
  sessionType: 'intake',
  topics: [],
  specialties: [],
  demographicPrefs: [],
  language: 'en',
  screeningEmergency: null,
  screeningHospitalization: null,
  screeningSuicidalIdeation: null,
  screeningMedications: null,
  screeningSubstanceUse: null,
  screeningLegalProceedings: null,
  format: null,
  availabilityTimeOfDay: [],
  availabilityDays: [],
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirth: '',
  zip: '',
  intakeLeadPatientId: null,
  payType: null,
  insuranceCompany: null,
  insurancePayerOrgId: null,
  selectedProviderId: null,
  bookingDate: null,
  bookingTime: null,
  screeningSlotStart: null,
  screeningSlotEnd: null,
  screeningPractitionerId: null,
  memberId: '',
  groupId: '',
  insuranceStartDate: '',
  insuranceEndDate: '',
  subscriberRelationship: 'self',
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDateOfBirth: '',
  stripeSetupIntentId: null,
  stripePaymentMethodId: null,
  stripePaymentMethodSummary: null,
  cashPaymentArrangedLater: false,
  preferredLocationIds: [],
  groupSelection: 'none',
  requestedGroupId: undefined,
  requestedGroupTemplateId: undefined,
  requestedGroupName: undefined,
  emailVerificationCode: '',
  bookings: [],
  currentReservation: null,
};

export type ScreeningField =
  | 'screeningEmergency'
  | 'screeningHospitalization'
  | 'screeningSuicidalIdeation'
  | 'screeningMedications'
  | 'screeningSubstanceUse'
  | 'screeningLegalProceedings';

export type IntakeAction =
  | { type: 'SET_THERAPY_TYPES'; payload: TherapyType[] }
  | { type: 'SET_THERAPY_TYPE'; payload: TherapyType }
  | { type: 'REMOVE_THERAPY_TYPE'; payload: TherapyType }
  | { type: 'SET_THERAPY_TYPE_CANCELLED'; payload: { therapyType: TherapyType; cancelled: boolean } }
  | { type: 'SET_THERAPY_TYPE_WAITLISTED'; payload: { therapyType: TherapyType; waitlisted: boolean } }
  | { type: 'SET_SKIPPED_AT_PROVIDER'; payload: { therapyType: TherapyType; skipped: boolean } }
  | { type: 'RESTORE_LAST_SKIPPED_AT_PROVIDER' }
  | { type: 'SET_SESSION_TYPE'; payload: SessionType }
  | { type: 'SET_TOPICS'; payload: string[] }
  | { type: 'SET_SPECIALTIES'; payload: string[] }
  | { type: 'SET_DEMOGRAPHIC_PREFS'; payload: string[] }
  | { type: 'SET_LANGUAGE'; payload: string }
  | { type: 'SET_SCREENING'; payload: { field: ScreeningField; value: boolean } }
  | { type: 'SET_FORMAT'; payload: FormatPreference }
  | { type: 'SET_AVAILABILITY_TIME_OF_DAY'; payload: string[] }
  | { type: 'SET_AVAILABILITY_DAYS'; payload: string[] }
  | { type: 'SET_FIRST_NAME'; payload: string }
  | { type: 'SET_LAST_NAME'; payload: string }
  | { type: 'SET_EMAIL'; payload: string }
  | { type: 'SET_DATE_OF_BIRTH'; payload: string }
  | { type: 'SET_ZIP'; payload: string }
  | { type: 'SET_INTAKE_LEAD_PATIENT_ID'; payload: string | null }
  | { type: 'SET_PAY_TYPE'; payload: PayType }
  | { type: 'SET_INSURANCE_PAYER'; payload: { orgId: string | null; name: string | null } }
  | { type: 'SET_SUBSCRIBER_RELATIONSHIP'; payload: SubscriberRelationship }
  | { type: 'SET_SUBSCRIBER_FIRST_NAME'; payload: string }
  | { type: 'SET_SUBSCRIBER_LAST_NAME'; payload: string }
  | { type: 'SET_SUBSCRIBER_DATE_OF_BIRTH'; payload: string }
  | { type: 'SET_SELECTED_PROVIDER_ID'; payload: string | null }
  | { type: 'SET_BOOKING_DATE'; payload: string | null }
  | { type: 'SET_BOOKING_TIME'; payload: string | null }
  | {
      type: 'SET_SCREENING_SLOT';
      payload: { start: string | null; end: string | null; practitionerId: string | null };
    }
  | { type: 'SET_MEMBER_ID'; payload: string }
  | { type: 'SET_GROUP_ID'; payload: string }
  | {
      type: 'SET_GROUP_THERAPY_SELECTION';
      payload: {
        groupSelection: 'none' | 'help-find' | 'group';
        requestedGroupId?: string;
        requestedGroupTemplateId?: string;
        requestedGroupName?: string;
        /** Cohort modality, used to derive `format` when the format step is skipped. */
        modality?: string;
      };
    }
  | { type: 'SET_INSURANCE_START_DATE'; payload: string }
  | { type: 'SET_INSURANCE_END_DATE'; payload: string }
  | {
    type: 'SET_STRIPE_PAYMENT';
    payload: {
      setupIntentId: string;
      paymentMethodId: string;
      summary: string;
    };
  }
  | { type: 'CLEAR_STRIPE_PAYMENT' }
  | { type: 'SET_CASH_PAYMENT_ARRANGED_LATER'; payload: boolean }
  | { type: 'SET_PREFERRED_LOCATION_IDS'; payload: string[] }
  | { type: 'SET_EMAIL_VERIFICATION_CODE'; payload: string }
  | { type: 'SET_EMAIL_VERIFIED'; payload: boolean }
  | { type: 'SET_EXISTING_CLIENT'; payload: boolean }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SAVE_CURRENT_BOOKING' }
  | { type: 'SAVE_SCREENING_CALL_BOOKING' }
  | { type: 'POP_LAST_BOOKING' }
  | { type: 'REMOVE_BOOKING'; payload: number }
  | { type: 'SET_BOOKING_CANCELLED'; payload: { index: number; cancelled: boolean } }
  | { type: 'SET_CURRENT_BOOKING_ROUND'; payload: number }
  | { type: 'SET_CURRENT_RESERVATION'; payload: BookingReservation | null }
  | { type: 'RESET'; payload: IntakeAnswers }
  | {
      type: 'RESTORE_INTAKE_RESUME';
      payload: {
        answers: Partial<IntakeAnswers>;
        patientId: string;
        step: number;
      };
    };

export interface IntakeState {
  answers: IntakeAnswers;
  activeStep: number;
  currentBookingRound: number;
}

export const TOTAL_STEPS = 14;

/** Whether the selected format requires the in-person location selection step. */
export function needsLocationStep(format: FormatPreference | null): boolean {
  return format === 'in-person' || format === 'hybrid';
}

/** Therapy types that are not cancelled on the cost-estimates step (used for booking rounds). */
export function getActiveTherapyTypes(answers: IntakeAnswers): TherapyType[] {
  const cancelled = answers.cancelledTherapyTypes ?? [];
  return answers.therapyTypes.filter((t) => !cancelled.includes(t));
}

/**
 * True if the client is high-risk. High-risk when they answered "yes" to suicidal ideation
 * or an active emergency/crisis, OR answered "yes" to 2+ of the six screening questions.
 */
export function isHighRisk(answers: IntakeAnswers): boolean {
  if (answers.screeningSuicidalIdeation === true || answers.screeningEmergency === true) {
    return true;
  }
  const yesCount = [
    answers.screeningEmergency,
    answers.screeningHospitalization,
    answers.screeningSuicidalIdeation,
    answers.screeningMedications,
    answers.screeningSubstanceUse,
    answers.screeningLegalProceedings,
  ].filter((value) => value === true).length;
  return yesCount >= 2;
}

/** Therapy types that never book a first therapy appointment in intake (screening / matching only). */
const NON_BOOKABLE_THERAPY_TYPES: TherapyType[] = ['iop', 'group'];

/** True when the client selected a concrete Group cohort (not help-find). */
export function hasSelectedGroupCohort(answers: IntakeAnswers): boolean {
  return answers.groupSelection === 'group' && Boolean(answers.requestedGroupId?.trim());
}

/**
 * True if the client needs to book a screening call during intake.
 *
 * Group Therapy is excluded: its screening is arranged from the ServiceRequest tracker after the
 * request is staged, so the client never picks a provider or a call time here.
 */
export function needsScreeningCall(answers: IntakeAnswers): boolean {
  if (isHighRisk(answers)) return true;
  return answers.therapyTypes.includes('iop');
}

/** True when Group Therapy is the only service requested. */
export function isGroupOnlyIntake(answers: IntakeAnswers): boolean {
  return answers.therapyTypes.length > 0 && answers.therapyTypes.every((t) => t === 'group');
}

/**
 * True when the flow will never produce an appointment: nothing to book and no screening call.
 * Group-only requests submit as pending ServiceRequests instead.
 */
export function intakeBooksNothing(answers: IntakeAnswers): boolean {
  return getProviderSelectionTherapyTypes(answers).length === 0 && !needsScreeningCall(answers);
}

/** Cohort modality → the format the client would otherwise have picked on the skipped step. */
export function groupModalityToFormat(modality: string | undefined): FormatPreference {
  const normalized = modality?.trim().toLowerCase();
  if (normalized === 'in-person' || normalized === 'in_person') return 'in-person';
  if (normalized === 'virtual' || normalized === 'telehealth') return 'virtual';
  if (normalized === 'hybrid') return 'hybrid';
  return 'no-preference';
}

/** Active therapy types that may book a first appointment (excludes Group / IOP). */
export function getNonScreeningTherapyTypes(answers: IntakeAnswers): TherapyType[] {
  return getActiveTherapyTypes(answers).filter((t) => !NON_BOOKABLE_THERAPY_TYPES.includes(t));
}

/** Therapy types that still need provider selection (excludes Group/IOP and skipped-at-provider). */
export function getProviderSelectionTherapyTypes(answers: IntakeAnswers): TherapyType[] {
  let types: TherapyType[];
  if (isHighRisk(answers)) {
    types = [];
  } else {
    // Group / IOP never enter the first-appointment booking rounds.
    types = getNonScreeningTherapyTypes(answers);
  }
  const skipped = answers.skippedAtProviderTherapyTypes ?? [];
  return types.filter((t) => !skipped.includes(t));
}

/** Therapy types that should receive a ServiceRequest on submit. */
export function getTherapyTypesForSubmission(answers: IntakeAnswers): TherapyType[] {
  const skipped = answers.skippedAtProviderTherapyTypes ?? [];
  return getActiveTherapyTypes(answers).filter((t) => !skipped.includes(t));
}

/** Practitioner ids already assigned to a non-cancelled booking (earlier service rounds). */
export function getBookedProviderIds(answers: IntakeAnswers): Set<string> {
  const ids = new Set<string>();
  for (const b of answers.bookings) {
    if (!b.cancelled && b.selectedProviderId) {
      ids.add(b.selectedProviderId);
    }
  }
  return ids;
}
